/**
 * =============================================================================
 * PayoutsTableView — Tabela reutilizável de pagamentos (módulo Indicação)
 *
 * Padronizado 1:1 com `CommissionsTableView`:
 *  - Tabela full-bleed (sem moldura externa).
 *  - Barra lateral colorida por status no início da linha (StatusBar helper).
 *  - Colunas com larguras fixas via <colgroup> + table-fixed.
 *  - Headers ordenáveis com chevrons, badges compactos, skeleton e estado vazio.
 *  - Ordenação local (página atual), espelhando ChargesTableView.
 *
 * @version 1.3.0
 * RELEASE:
 *   2026-04-29 v1.0.0 - Criação para padronizar a listagem de Pagamentos
 *                       com Comissões.
 *   2026-04-29 v1.1.0 - Substituiu coluna "Origem" por "Nº Negócio",
 *                       "Cliente" e "Nº Indicação" assumindo 1 comissão
 *                       por pagamento (premissa errada).
 *   2026-04-30 v1.2.0 - Removidos tooltips que expunham UUID completo.
 *   2026-04-30 v1.3.0 - REFATOR: removidas as colunas "Nº Negócio",
 *                       "Cliente" e "Nº Indicação" (achatavam a relação
 *                       1:N entre payout e commissions). A coluna
 *                       "# Comissões" virou pill clicável que abre o
 *                       `PayoutCommissionsModal` (drill-down completo).
 *                       Removidas props `onOpenDeal` / `onOpenCustomer`
 *                       desta tabela — passam a viver no modal e no Sheet.
 * =============================================================================
 */
import React, { useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Wallet,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  PlayCircle,
  StickyNote,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  PayoutStatusBadge,
  PAYOUT_STATUS_CONFIG,
} from './PayoutStatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { StatusBar } from '@/components/referral/shared/StatusBar';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import type {
  PaginatedPayout,
  PayoutStatus,
  PayoutMethod,
} from '@/hooks/useReferralPayoutsPaginated';

// ============================================================================
// CONSTANTES E MAPEAMENTOS
// ============================================================================

/** Ações disponíveis no menu de cada linha. */
export type PayoutRowAction =
  | 'approve'        // Iniciar processamento (pending → processing)
  | 'mark_paid'      // Marcar como pago (abre diálogo)
  | 'mark_failed'    // Marcar como falho (abre diálogo)
  | 'cancel';        // Cancelar (abre diálogo)

// Mapa de status migrado para `PayoutStatusBadge` (fonte única de verdade
// para label/cor de status de pagamento). Cor da barra lateral é derivada
// de `PAYOUT_STATUS_CONFIG` em `getPayoutStatusBarColor`.

/** Labels de método de pagamento (PT-BR). */
const METHOD_LABEL: Record<PayoutMethod, string> = {
  pix: 'PIX',
  bank_transfer: 'Transferência',
  manual: 'Manual',
  credits: 'Crédito',
};

/** Campos suportados pela ordenação local (página atual). */
type SortField =
  | 'affiliate'
  | 'amount'
  | 'status'
  | 'requested_at'
  | 'paid_at';

type SortDirection = 'asc' | 'desc';

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/** Cor da barra lateral por status — derivada de `PAYOUT_STATUS_CONFIG`. */
function getPayoutStatusBarColor(status: PayoutStatus): string {
  return PAYOUT_STATUS_CONFIG[status]?.bar ?? 'bg-muted-foreground/40';
}

/** Formata valor monetário em BRL (moeda fixa do módulo). */
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

// (v1.3.0) `formatCpfCnpj` removido — não há mais coluna de Cliente nesta
// tabela. A formatação agora vive no `PayoutCommissionsModal`, onde cada
// comissão é renderizada em sua própria linha.

// ============================================================================
// COMPONENTE: HEADER ORDENÁVEL (cópia 1:1 do CommissionsTableView)
// ============================================================================

function SortableHeader({
  field,
  label,
  currentField,
  direction,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const isActive = currentField === field;
  return (
    <TableHead
      className={cn(
        'text-xs font-medium py-2 cursor-pointer hover:bg-muted/30 transition-colors select-none',
        align === 'right' && 'text-right pr-4',
        align === 'center' && 'text-center',
      )}
      onClick={() => onSort(field)}
    >
      <div
        className={cn(
          'flex items-center gap-1',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

// ============================================================================
// COMPONENTE: LINHA DA TABELA
// ============================================================================

function PayoutTableRow({
  payout,
  highlightId,
  onAction,
  onOpenDetails,
  onOpenCommissions,
}: {
  payout: PaginatedPayout;
  highlightId?: string | null;
  onAction: (payout: PaginatedPayout, action: PayoutRowAction) => void;
  /** v1.3.0 — Abre o Sheet lateral de detalhes do pagamento. */
  onOpenDetails?: (payoutId: string) => void;
  /**
   * v1.3.0 — Abre o modal de drill-down `PayoutCommissionsModal` listando
   * todas as comissões vinculadas a este pagamento. Substitui as antigas
   * colunas Negócio/Cliente/Indicação que assumiam 1 comissão por payout.
   */
  onOpenCommissions?: (payout: PaginatedPayout) => void;
}) {
  // Status visual agora é renderizado pelo `PayoutStatusBadge` (dot + texto).

  const isHighlighted = payout.id === highlightId;

  return (
    <TableRow
      className={cn(
        'hover:bg-muted/40 border-b border-border/40 group transition-colors',
        isHighlighted && 'bg-primary/5 ring-2 ring-primary/40 ring-inset',
      )}
    >
      {/* Barra lateral colorida por status */}
      <TableCell className="p-0 w-2">
        <StatusBar colorClass={getPayoutStatusBarColor(payout.status)} />
      </TableCell>

      {/* Ref. — 8 primeiros dígitos do UUID. Clicável quando há
          handler `onOpenDetails`, abrindo o Sheet lateral de detalhes. */}
      <TableCell className="px-2 py-1.5">
        {onOpenDetails ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(payout.id);
            }}
            className="font-mono text-[11px] text-primary hover:underline cursor-pointer uppercase"
            title="Ver detalhes do pagamento"
          >
            {payout.id.substring(0, 8).toUpperCase()}
          </button>
        ) : (
          <span className="font-mono text-[11px] text-muted-foreground">
            {payout.id.substring(0, 8).toUpperCase()}
          </span>
        )}
      </TableCell>

      {/* Afiliado (nome + chave PIX/email) */}
      <TableCell className="px-2 py-1.5 max-w-[180px]">
        <div className="font-medium text-sm truncate">
          {payout.affiliate_full_name ?? '—'}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {payout.affiliate_pix_key
            ? `PIX (${payout.affiliate_pix_key_type ?? '—'}): ${payout.affiliate_pix_key}`
            : payout.affiliate_email}
        </div>
      </TableCell>

      {/* Método */}
      <TableCell className="px-2 py-1.5">
        <Badge variant="outline" className="text-[10px] h-5">
          {METHOD_LABEL[payout.payout_method] ?? payout.payout_method}
        </Badge>
      </TableCell>

      {/* Comissões — pill clicável que abre o modal de drill-down.
          Mesmo quando há apenas 1 comissão o botão segue clicável (consistência
          visual: o usuário sempre acessa as comissões pelo mesmo caminho). */}
      <TableCell className="px-2 py-1.5">
        {onOpenCommissions ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenCommissions(payout);
            }}
            title="Ver comissões deste pagamento"
            className={cn(
              'inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/30',
              'px-2 py-0.5 text-[11px] font-medium tabular-nums text-foreground',
              'hover:border-primary/60 hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer',
            )}
          >
            {payout.commissions_count}{' '}
            {payout.commissions_count === 1 ? 'comissão' : 'comissões'}
            <ChevronRight className="h-3 w-3 opacity-70" />
          </button>
        ) : (
          <span className="text-xs text-muted-foreground tabular-nums">
            {payout.commissions_count}
          </span>
        )}
      </TableCell>

      {/* Valor */}
      <TableCell className="text-right px-2 py-1.5">
        <span className="text-sm font-medium tabular-nums">
          {formatBRL(payout.amount)}
        </span>
      </TableCell>

      {/* Status — padrão "dot + texto" (PayoutStatusBadge) */}
      <TableCell className="px-2 py-1.5">
        <PayoutStatusBadge status={payout.status} />
      </TableCell>

      {/* Solicitado em */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {format(new Date(payout.requested_at), 'dd/MM/yy', { locale: ptBR })}
      </TableCell>

      {/* Pago em */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {payout.paid_at
          ? format(new Date(payout.paid_at), 'dd/MM/yy', { locale: ptBR })
          : '-'}
      </TableCell>

      {/* Observações */}
      <TableCell className="px-2 py-1.5">
        {payout.notes ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 text-xs text-muted-foreground max-w-[140px]">
                  <StickyNote className="h-3 w-3 shrink-0" />
                  <span className="truncate">{payout.notes}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs whitespace-pre-wrap">
                {payout.notes}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* Ações */}
      <TableCell className="px-2 py-1.5 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* v1.3.0 — Atalho para abrir o Sheet lateral de detalhes. */}
            {onOpenDetails && (
              <>
                <DropdownMenuItem onClick={() => onOpenDetails(payout.id)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Ver detalhes
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {payout.status === 'pending' && (
              <DropdownMenuItem onClick={() => onAction(payout, 'approve')}>
                <PlayCircle className="h-4 w-4 mr-2" />
                Iniciar processamento
              </DropdownMenuItem>
            )}
            {(payout.status === 'pending' || payout.status === 'processing') && (
              <DropdownMenuItem onClick={() => onAction(payout, 'mark_paid')}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Marcar como pago
              </DropdownMenuItem>
            )}
            {(payout.status === 'pending' || payout.status === 'processing') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onAction(payout, 'mark_failed')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Marcar como falho
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onAction(payout, 'cancel')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancelar
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

interface PayoutsTableViewProps {
  /** Lista de pagamentos da página atual (server-side). */
  payouts: PaginatedPayout[];
  /** Estado de loading (skeleton). */
  isLoading: boolean;
  /** Callback ao acionar item do menu da linha. */
  onAction: (payout: PaginatedPayout, action: PayoutRowAction) => void;
  /** ID do payout a ser destacado (deep-link). */
  highlightId?: string | null;
  /** Mensagem para estado vazio. */
  emptyMessage?: string;
  /** v1.3.0 — Abre o Sheet lateral de detalhes do pagamento. */
  onOpenDetails?: (payoutId: string) => void;
  /**
   * v1.3.0 — Abre o `PayoutCommissionsModal` listando todas as comissões
   * vinculadas ao pagamento clicado (drill-down 1:N).
   */
  onOpenCommissions?: (payout: PaginatedPayout) => void;
}

/**
 * Tabela reutilizável de pagamentos — paridade visual 1:1 com `CommissionsTableView`.
 */
export function PayoutsTableView({
  payouts,
  isLoading,
  onAction,
  highlightId,
  emptyMessage = 'Nenhuma solicitação de saque encontrada',
  onOpenDetails,
  onOpenCommissions,
}: PayoutsTableViewProps) {
  // ========== ESTADOS LOCAIS ==========
  const [sortField, setSortField] = useState<SortField>('requested_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ========== ORDENAÇÃO LOCAL (página atual) ==========
  const sortedPayouts = useMemo(() => {
    return [...payouts].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'affiliate':
          comparison = (a.affiliate_full_name || '').localeCompare(b.affiliate_full_name || '');
          break;
        case 'amount':
          comparison = Number(a.amount) - Number(b.amount);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'requested_at':
          comparison = new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime();
          break;
        case 'paid_at': {
          const da = a.paid_at ? new Date(a.paid_at).getTime() : 0;
          const db = b.paid_at ? new Date(b.paid_at).getTime() : 0;
          comparison = da - db;
          break;
        }
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [payouts, sortField, sortDirection]);

  /** Alterna ordenação ao clicar no header. */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ========== SKELETON LOADING ==========
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // ========== ESTADO VAZIO ==========
  if (payouts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center">
          <Wallet className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="text-center">
          <h3 className="font-medium text-sm">{emptyMessage}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Tente ajustar os filtros aplicados.
          </p>
        </div>
      </div>
    );
  }

  // ========== MIN-WIDTH DA TABELA ==========
  // v1.3.0 — removidas as 3 colunas Negócio/Cliente/Indicação (365px). A coluna
  // "Comissões" virou pill clicável (drill-down). Total recalculado.
  const minWidth = '1180px';

  return (
    <ScrollIndicators className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-full">
      <div ref={scrollContainerRef} data-scroll-viewport className="flex-1 min-h-0 w-full max-w-full overflow-auto">
        <table
          className="w-max caption-bottom text-sm table-fixed"
          style={{ minWidth }}
        >
          {/* Larguras de colunas (v1.3.0). */}
          <colgroup>
            <col style={{ width: 8 }} />   {/* Barra de status */}
            <col className="w-[75px]" />   {/* Ref. */}
            <col className="w-[200px]" />  {/* Afiliado */}
            <col className="w-[100px]" />  {/* Método */}
            <col className="w-[140px]" />  {/* Comissões (pill clicável) */}
            <col className="w-[110px]" />  {/* Valor */}
            <col className="w-[100px]" />  {/* Status */}
            <col className="w-[85px]" />   {/* Solicitado em */}
            <col className="w-[85px]" />   {/* Pago em */}
            <col className="w-[140px]" />  {/* Observações */}
            <col className="w-[60px]" />   {/* Ações */}
          </colgroup>

          {/* Header sticky */}
          <TableHeader className="sticky top-0 bg-background z-[1]">
            <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
              <TableHead className="w-2 p-0" />
              <TableHead className="text-xs font-medium px-2 py-2">Ref.</TableHead>
              <SortableHeader
                field="affiliate"
                label="Afiliado"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <TableHead className="text-xs font-medium px-2 py-1.5">Método</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1.5">
                Comissões
              </TableHead>
              <SortableHeader
                field="amount"
                label="Valor"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                field="status"
                label="Status"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                field="requested_at"
                label="Solicitado"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                field="paid_at"
                label="Pago em"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <TableHead className="text-xs font-medium px-2 py-1.5">
                Observações
              </TableHead>
              <TableHead className="text-xs font-medium text-right px-2 py-1.5">
                Ações
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedPayouts.map((p) => (
              <PayoutTableRow
                key={p.id}
                payout={p}
                highlightId={highlightId}
                onAction={onAction}
                onOpenDetails={onOpenDetails}
                onOpenCommissions={onOpenCommissions}
              />
            ))}
          </TableBody>
        </table>
      </div>
    </ScrollIndicators>
  );
}
